import { useCallback, useEffect, useRef, useState } from "react";
import useMarketPlacesAutocomplete from "../../hooks/useMarketPlacesAutocomplete";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import locationBg from "../../assets/img/locationBg.png";
import mapStyles from "./mapStyles";
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from "@reach/combobox";
import "@reach/combobox/styles.css";
import Layout from "../../components/Global/Layout";
import CheckoutStepper from "../../components/CheckoutStepper";
import { saveShippingAddress } from "../../redux/brandCartSlice";
import SavedAddressPicker from "../../components/Address/SavedAddressPicker";
import { isWithinMarketBounds, MAP_CENTER, normalizeMarketLatLng } from "../../config/market";
import { resolveInitialDeliveryPhone, saveLastDeliveryPhone, accountPhone } from "../../utils/deliveryPhone";
import { updateMyProfile } from "../../api/profileApi";
import { loginSuccess } from "../../redux/userRedux";
import { toast } from "react-toastify";
import useCoverage from "../../redux/useCoverage";
import { cartOutletId } from "../../utils/vendorOutlets";
import CoverageBanner from "../../components/CoverageBanner";
import GoogleMapBoundary from "../../components/Maps/GoogleMapBoundary";
import MapPin from "../../components/Maps/MapPin";
import { geocodeAddress, getPlaceSummary, latLngFromGeocode } from "../../utils/googlePlaces";
import reportClientError from "../../utils/clientTelemetry";
import {
    evaluateShippingGate,
    isValidDeliveryPhone,
    savedAddressLatLng,
    shippingGateLabel,
} from "../../utils/shippingValidation";

const Button = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.5)
    ),
    url(${locationBg});
  background-position: center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px 20px 20px;
`;

const Wrapper = styled.div`
  width: 50%;
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.9);

  @media (max-width: 768px) {
    width: 87%;
    padding: 10px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 300;
  font-family: "Poppins", sans-serif;
  color: #000B0D;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
`;

const Input = styled.input`
  flex: 1;
  min-width: 40%;
  margin: 10px 0px;
  padding: 10px;
  border: 1px solid red;
  border-radius: 8px;
  outline: none;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.9);
  background-color: #e2e2e2c4;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const mapContainerStyle = {
    width: "100%",
    height: "400px",
};

const options = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: mapStyles,
};

const BrandShippingScreen = () => {
    const {
        value,
        suggestions: { data },
        setValue,
        clearSuggestions,
        ready: placesReady,
        error: placesError,
    } = useMarketPlacesAutocomplete({ debounce: 100 });

    const [customerNumber, setCustomerNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [locationId, setLocationId] = useState("");
    const [locationName, setLocationName] = useState([]);
    const [latLng, setLatLng] = useState(MAP_CENTER);
    const [hasChosenAddress, setHasChosenAddress] = useState(false);
    const [saveToProfile, setSaveToProfile] = useState(false);
    const currentBrand = useSelector((state) => state.brandCart.currentBrand);
    const currentCart = useSelector(
        (state) => state.brandCart.carts[currentBrand] || {}
    );
    // The outlet this cart is fulfilled from (off its lines, as the
    // restaurant shipping screen reads it); null for the main store.
    const outletId = cartOutletId(currentCart);
    const userLocation = useSelector((state) => state.userLocation);

    // An EXPLICIT pick outranks the saved primary. `locationSource:'manual'` is
    // set only by setManualAddress — the shopper typed and chose a place. A
    // gps/ip guess is a much weaker signal about where they want a delivery, so
    // the saved primary still wins there.
    const browseLocationIsExplicit =
        userLocation?.locationSource === "manual" &&
        isWithinMarketBounds(userLocation?.latitude, userLocation?.longitude);
    const currentUser = useSelector((state) => state.user?.currentUser);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
    const [noResultsMessage, setNoResultsMessage] = useState("");
    const [addressError, setAddressError] = useState("");
    const timeoutRef = useRef(null);
    const typingTimer = useRef();
    const dispatch = useDispatch();
    const history = useHistory();

    // Pre-checkout coverage gate (backend /api/coverage — same decision as the
    // order-create gate), re-checked against the address just typed here (not
    // just the browse-time location) so the customer can't submit an
    // undeliverable shipping address.
    const coverage = useCoverage({
        vendorId: currentBrand,
        outletId,
        vendorType: "brand",
        enabled: hasChosenAddress,
        lat: latLng?.lat,
        lng: latLng?.lng,
    });

    const phonePrefilledRef = useRef(false);
    useEffect(() => {
        if (phonePrefilledRef.current) return;
        const prefill = resolveInitialDeliveryPhone(currentUser);
        if (prefill) {
            phonePrefilledRef.current = true;
            setCustomerNumber(String(prefill));
            setPhoneError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const prefilledRef = useRef(false);
    useEffect(() => {
        if (prefilledRef.current) return;
        const rawLat = userLocation?.latitude;
        const rawLng = userLocation?.longitude;
        if (isWithinMarketBounds(rawLat, rawLng)) {
            const lat = Number(rawLat);
            const lng = Number(rawLng);
            prefilledRef.current = true;
            setLatLng({ lat, lng });
            setLocationName({
                name: userLocation.address || "Your set location",
                vicinity: userLocation.address || "",
            });
            setValue(userLocation.address || "", false);
            setHasChosenAddress(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation?.latitude, userLocation?.longitude]);

    useEffect(() => {
        if (locationId) {
            getPlaceSummary(locationId)
                .then((res) => {
                    setLocationName((current) => ({
                        name: res.name || current?.name || "",
                        vicinity: res.vicinity || current?.vicinity || "",
                    }));
                })
                .catch((err) => {
                    reportClientError(err, { area: "brand_place_details" });
                });
        }
    }, [locationId]);

    useEffect(() => {
        if (placesError) {
            setAddressError(
                "Address search is temporarily unavailable. Retry the map or use a valid saved address."
            );
        }
    }, [placesError]);

    useEffect(() => {
        if (fetchingSuggestions) {
            clearTimeout(typingTimer.current);
            setNoResultsMessage("Searching...");
        } else {
            clearTimeout(typingTimer.current);
            typingTimer.current = setTimeout(() => {
                if (data.length === 0 && value.length > 0 && !locationName.name) {
                    setNoResultsMessage("Address not found!");
                } else {
                    setNoResultsMessage("");
                }
            }, 2000);
        }
    }, [data, fetchingSuggestions, value, locationName]);

    // Pre-fill the manual form from a chosen saved address so its latLng flows
    // into saveShippingAddress (delivery coverage + fee read shippingAddress.latLng).
    const handleSelectSavedAddress = (addr) => {
        if (!addr) return;
        const coordinates = savedAddressLatLng(addr);
        if (!coordinates) {
            setHasChosenAddress(false);
            setAddressError("This saved address needs an updated location pin before checkout.");
            return;
        }
        if (addr.recipientPhone) {
            setCustomerNumber(addr.recipientPhone);
            setPhoneError("");
        }
        setAddressError("");
        setLocationName({ name: addr.label || "Saved address", vicinity: addr.address || addr.description || "" });
        setLatLng(coordinates);
        setHasChosenAddress(true);
        setLocationId("");
        setValue(addr.address || "", false);
    };

    const shippingGate = evaluateShippingGate({
        phone: customerNumber,
        address: locationName?.name,
        lat: latLng?.lat,
        lng: latLng?.lng,
        hasChosenAddress,
        vendorId: currentBrand,
        coverage,
    });
    const canContinue = shippingGate.canContinue;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canContinue) return;
        saveLastDeliveryPhone(customerNumber);
        if (saveToProfile && customerNumber.replace(/\D/g, "").length >= 9) {
            updateMyProfile({ phoneNumber: customerNumber })
                .then((data) => {
                    // Only refresh the redux user when a token is available —
                    // dispatching loginSuccess without one falls through to the
                    // raw-payload branch of the reducer and corrupts currentUser
                    // into a nested shape (silently breaking auth headers).
                    const refreshedToken =
                        data.accessToken ||
                        currentUser?.accessToken ||
                        currentUser?.token ||
                        currentUser?.result?.accessToken ||
                        currentUser?.result?.token;
                    if (refreshedToken) {
                        dispatch(
                            loginSuccess({
                                user: { ...currentUser, ...(data.user || {}), phoneNumber: customerNumber },
                                accessToken: refreshedToken,
                            })
                        );
                    }
                    toast.success("Saved your number to your profile.");
                })
                .catch((err) =>
                    toast.error(
                        err?.response?.data?.message ||
                        "Couldn't save the number to your profile, but your order will still use it."
                    )
                );
        }
        dispatch(
            saveShippingAddress({
                customerNumber,
                locationName,
                latLng,
                source: "places", // pin is the geocoder point for the picked address, not a GPS fix
                locationId,
            })
        );
        history.push("/brand-payment");
    };

    const boxOptionRef = useRef();

    const panTo = useCallback(({ lat, lng }) => {
        // console.log(lat, lng, " lat lng");
    }, []);

    const handleSelect = async (value) => {
        setValue(value, false);
        clearSuggestions();

        try {
            const results = await geocodeAddress(value);
            const raw = latLngFromGeocode(results[0]);
            const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
            if (!coordinates) throw new Error("The selected address is outside this delivery market");
            setLocationId(results[0].place_id);
            panTo(coordinates);
            setLatLng(coordinates);
            setHasChosenAddress(true);
            setAddressError("");
        } catch (error) {
            reportClientError(error, { area: "brand_shipping_geocode" });
            setHasChosenAddress(false);
            setAddressError("We couldn’t pin that address. Choose another suggestion and retry.");
        }
    };

    const handleInput = (e) => {
        setValue(e.target.value);
        setFetchingSuggestions(true);
        setAddressError("");

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setFetchingSuggestions(false);
        }, 100);
    };

    const renderSuggestions = () =>
        data.map((suggestion) => {
            const {
                place_id,
                structured_formatting: { main_text, secondary_text },
            } = suggestion;
            return (
                <ComboboxOption
                    key={place_id}
                    value={main_text + " " + secondary_text}
                />
            );
        });

    const clearRenderedLocationDetails = () => {
        clearSuggestions();
        setLatLng(MAP_CENTER);
        setValue("");
        setLocationId("");
        setLocationName("");
        setHasChosenAddress(false);
        setAddressError("");
    };

    const handlePhoneChange = (e) => {
        const phone = e.target.value;
        setCustomerNumber(phone);

        if (!isValidDeliveryPhone(phone)) {
            setPhoneError(
                "*Phone number must be at least 10 digits long [e.g 0700123123]"
            );
        } else {
            setPhoneError("");
        }
    };

    return (
        <Layout>
            <Container>
                <Wrapper>
                    <CheckoutStepper currentStep="shipping" />
                    <Title>SHIPPING ADDRESS</Title>
                    <SavedAddressPicker
                        autoSelect={!browseLocationIsExplicit}
                        onSelect={handleSelectSavedAddress}
                        onDeselect={clearRenderedLocationDetails}
                    />
                    <Form>
                        <Input
                            value={customerNumber}
                            onChange={handlePhoneChange}
                            placeholder="Enter Contact Phone number..."
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            required
                        />
                        {phoneError && (
                            <p
                                style={{
                                    color: "red",
                                    fontSize: "10px",
                                    margin: "0",
                                    padding: "0",
                                    textAlign: "left",
                                    fontStyle: "italic",
                                }}
                            >
                                {phoneError}
                            </p>
                        )}
                        {customerNumber.replace(/\D/g, "").length >= 9 &&
                            customerNumber.trim() !== String(accountPhone(currentUser) || "").trim() && (
                                <label
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        alignSelf: "flex-start",
                                        gap: "8px",
                                        fontSize: "13px",
                                        color: "#334",
                                        margin: "4px 2px 6px",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        width: "auto",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={saveToProfile}
                                        onChange={(e) => setSaveToProfile(e.target.checked)}
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            minWidth: "16px",
                                            margin: 0,
                                            flex: "none",
                                            cursor: "pointer",
                                        }}
                                    />
                                    <span>Save this number to my profile for next time</span>
                                </label>
                            )}
                        <GoogleMapBoundary height="400px">
                          <GoogleMap
                              mapContainerStyle={mapContainerStyle}
                              zoom={12}
                              center={latLng}
                              options={options}
                          >
                            <div
                                style={{
                                    display: "flex",
                                    alignSelf: "center",
                                    flexDirection: "row",
                                    marginTop: "5px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "80%",
                                        zIndex: "3",
                                        margin: "0",
                                    }}
                                >
                                    <Combobox ref={boxOptionRef} onSelect={handleSelect}>
                                        <div style={{ position: "relative" }}>
                                            <ComboboxInput
                                                value={value}
                                                onChange={handleInput}
                                                disabled={!placesReady}
                                                placeholder={
                                                    placesReady
                                                        ? "Enter Delivery Address..."
                                                        : "Loading address search…"
                                                }
                                                style={{
                                                    width: "100%",
                                                    padding: "10px",
                                                    border: "1px solid #f58c60",
                                                    borderRadius: "5px",
                                                    zIndex: "3",
                                                    boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.5)",
                                                    backgroundColor: "#e2e2e2c4",
                                                    outline: "none",
                                                }}
                                            />
                                            {fetchingSuggestions ||
                                                (data.length === 0 && value.length > 0) ? (
                                                <>
                                                    <p
                                                        style={{
                                                            color: "red",
                                                            fontSize: "11px",
                                                            fontStyle: "italic",
                                                            border: "1px solid #ccc",
                                                            backgroundColor: "#f8f8f8",
                                                            borderRadius: "4px",
                                                            padding: "5px",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        {noResultsMessage}
                                                    </p>
                                                </>
                                            ) : null}
                                        </div>
                                        <ComboboxPopover
                                            style={{
                                                backgroundColor: "white",
                                                margin: "0",
                                                padding: "0",
                                                fontSize: "12px",
                                                border: "1px solid #F58C60",
                                                fontStyle: "italic",
                                                boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.8)",
                                                borderBottomRightRadius: "10px",
                                                borderBottomLeftRadius: "10px",
                                            }}
                                        >
                                            <ComboboxList
                                                style={{
                                                    marginLeft: "10px",
                                                    padding: "0",
                                                    overflow: "auto",
                                                    maxHeight: "200px",
                                                    lineHeight: "2",
                                                }}
                                            >
                                                {renderSuggestions()}
                                            </ComboboxList>
                                        </ComboboxPopover>
                                    </Combobox>
                                    {addressError && (
                                        <p role="alert" style={{ color: "#b3362a", fontSize: "11px" }}>
                                            {addressError}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {hasChosenAddress && <MapPin position={latLng} label="Delivery location" />}
                            {hasChosenAddress && locationName?.name && (
                                <InfoWindow
                                    position={latLng}
                                    onCloseClick={() => clearRenderedLocationDetails()}
                                >
                                    <div
                                        style={{
                                            paddingBottom: "5px",
                                        }}
                                    >
                                        <h3
                                            style={{
                                                color: "orange",
                                                fontSize: "15px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            <span role="img" aria-label="bear">
                                                📌
                                            </span>{" "}
                                            Set Delivery Address
                                        </h3>
                                        <p
                                            style={{
                                                textAlign: "center",
                                                fontStyle: "italic",
                                                fontSize: "10px",
                                            }}
                                        >
                                            <span>
                                                {locationName.name} ~ {locationName.vicinity}
                                            </span>
                                        </p>
                                    </div>
                                </InfoWindow>
                            )}
                          </GoogleMap>
                        </GoogleMapBoundary>

                        <CoverageBanner
                            variant="block"
                            deliverable={coverage.deliverable}
                            reason={coverage.reason}
                            distanceKm={coverage.distanceKm}
                            radius={coverage.radius}
                            coverageCountry={coverage.coverageCountry}
                            hasLocation={coverage.hasLocation}
                            status={coverage.status}
                            error={coverage.error}
                            onRetry={coverage.refresh}
                        />

                        <Button
                            disabled={!canContinue}
                            onClick={handleSubmit}
                        >
                            {shippingGateLabel(shippingGate.code)}
                        </Button>
                    </Form>
                </Wrapper>
            </Container>
        </Layout>
    );
};

export default BrandShippingScreen;
